/**
 * ScheduleRunModal — one-time "run at a future date/time" picker, shared by
 * AgentDetailsPage (agent schedules) and Workflows.jsx (chain/workflow
 * schedules). Both go through the same backend primitive
 * (agentRunAPI.scheduleRun / getScheduled / cancelScheduled — an AgentRun
 * document with status "scheduled" and either an agentId or a chainId), so
 * whatever gets scheduled here shows up automatically on the Schedule page
 * and Home's "Today's schedule" panel, which already read from
 * agentRunAPI.getScheduled().
 *
 * There is no recurrence option here — the backend only supports a single
 * scheduledAt timestamp per run (recurring cron schedules are a separate,
 * flow-only system: flowScheduleAPI). Only one-time scheduling is offered
 * so the UI doesn't promise something the backend can't do.
 */
import React, { useState } from "react";
import { X, CalendarClock } from "lucide-react";
import { agentRunAPI } from "../../services/api";
import toast from "react-hot-toast";

const T = {
  surface: "var(--lyra-color-surface)",
  surface2: "var(--lyra-color-surface-2)",
  border: "var(--lyra-color-border)",
  ink: "var(--lyra-color-text)",
  ink2: "var(--app-text-2)",
  ink4: "var(--app-text-3)",
  accent: "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid: "var(--app-primary-soft-2)",
  red: "var(--lyra-color-danger)",
  redBg: "var(--lyra-color-danger-bg)",
  redBd: "var(--app-danger-border)",
};

function pad(n) { return String(n).padStart(2, "0"); }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function toTimeStr(d) { return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }

export default function ScheduleRunModal({ target, targetType, existingRun, onClose, onScheduled, onCancelExisting, initialDate }) {
  const defaultAt = existingRun?.scheduledAt ? new Date(existingRun.scheduledAt) : new Date(Date.now() + 60 * 60 * 1000);
  // initialDate seeds only the date field (e.g. a date picked in a calendar
  // sidebar) — the default time-of-day behavior above is unchanged.
  const [dateStr, setDateStr] = useState(initialDate ? toDateStr(new Date(initialDate)) : toDateStr(defaultAt));
  const [timeStr, setTimeStr] = useState(toTimeStr(defaultAt));
  const [input, setInput] = useState(existingRun?.input || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isChain = targetType === "chain";
  const name = target?.name || (isChain ? "Workflow" : "Agent");

  const handleSchedule = async () => {
    const scheduledAt = new Date(`${dateStr}T${timeStr}`);
    if (isNaN(scheduledAt.getTime())) { setErr("Invalid date/time"); return; }
    if (scheduledAt <= new Date()) { setErr("Please pick a future date and time"); return; }
    setSaving(true);
    setErr("");
    try {
      if (existingRun?._id) {
        try {
          await agentRunAPI.cancelScheduled(existingRun._id);
          onCancelExisting && onCancelExisting(existingRun._id);
        } catch (_) { /* non-fatal — proceed to create the new schedule anyway */ }
      }
      const payload = {
        agentName: name,
        input: input || "",
        scheduledAt: scheduledAt.toISOString(),
        ...(isChain ? { chainId: target._id || target.id } : { agentId: target._id || target.id }),
      };
      const data = await agentRunAPI.scheduleRun(payload);
      try { window.dispatchEvent(new CustomEvent("lyra:agent-scheduled", { detail: data.run || data })); } catch (_) {}
      toast.success(`${isChain ? "Workflow" : "Agent"} scheduled`);
      onScheduled && onScheduled(data.run || data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Failed to schedule");
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: "100%", padding: "9px 12px", borderRadius: 9,
    border: `1px solid ${T.border}`, background: T.surface2,
    color: T.ink, fontSize: 13.5, outline: "none",
    fontFamily: "inherit", boxSizing: "border-box",
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.25)" }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        zIndex: 201, width: 340, background: T.surface, borderRadius: 16,
        border: `1px solid ${T.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.15)", padding: "20px 22px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: T.accentLight, border: `1px solid ${T.accentMid}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CalendarClock size={16} color={T.accent} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{existingRun ? "Reschedule run" : "Schedule run"}</div>
              <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 1, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.ink4, padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.ink4, marginBottom: 6 }}>Date</div>
          <input type="date" value={dateStr} min={toDateStr(new Date())} onChange={e => setDateStr(e.target.value)} style={inp} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.ink4, marginBottom: 6 }}>Time</div>
          <input type="time" value={timeStr} onChange={e => setTimeStr(e.target.value)} style={inp} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.ink4, marginBottom: 6 }}>
            Instructions <span style={{ textTransform: "none", fontWeight: 400 }}>(optional)</span>
          </div>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={isChain ? "What should this workflow do when it runs?" : "What should this agent do when it runs?"}
            rows={2}
            style={{ ...inp, resize: "vertical", minHeight: 50, fontFamily: "inherit" }}
          />
        </div>

        {dateStr && timeStr && (() => {
          const dt = new Date(`${dateStr}T${timeStr}`);
          if (!isNaN(dt.getTime()) && dt > new Date()) {
            return (
              <div style={{ background: T.accentLight, border: `1px solid ${T.accentMid}`, borderRadius: 9, padding: "8px 12px", marginBottom: 14, fontSize: 12.5, color: T.accent }}>
                🗓 {dt.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
              </div>
            );
          }
          return null;
        })()}

        {err && (
          <div style={{ background: T.redBg, border: `1px solid ${T.redBd}`, borderRadius: 8, padding: "7px 11px", marginBottom: 12, fontSize: 12, color: T.red }}>{err}</div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface2, color: T.ink2, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={saving}
            style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: `1px solid ${T.accentMid}`, background: T.accentLight, color: T.accent, fontSize: 13, fontWeight: 600, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}
          >
            {saving ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </div>
    </>
  );
}
